import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {Dispatch, DispatchDialogData, Program} from '../../../../domain/cloud-topology';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import moment from 'moment-timezone';
import 'moment-timezone';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {ProgramService} from "../../../../services/vpp/program.service";
import {cancelStatus, errorStatus, saveStatus} from "../../../../domain/constants";
import {map, startWith} from "rxjs/operators";
import {ResourceService} from "../../../../services/resource.service";
@Component({
    selector: 'app-dispatch-new',
    templateUrl: './dispatch-new.component.html',
    styleUrls: ['./dispatch-new.component.scss'],
    standalone: false
})
export class DispatchNewComponent implements OnInit, OnDestroy {
  dispatchForm: FormGroup;
  dispatch: Dispatch;
  program: Program;
  programs: Program[];
  timezone: string;
  parsedDuration: string;
  loading: boolean;
  durationTypes;
  durationType;
  date: string;
  minDate: {year: number, month: number, day: number};
  pm: boolean;
  searchOptions: string[] = [];
  filteredOptions: Observable<string[]>;
  searchControl = new FormControl();
  targetId = new FormControl();
  startTime: {hour: number, minute: number};

  private subject$: Subject<void>;
  constructor(private programService: ProgramService,
              public dialogRef: MatDialogRef<MatDialogRef<DispatchNewComponent>>,
              @Inject(MAT_DIALOG_DATA) public dialogData: DispatchDialogData) {}

  private _filter(value: string): string[] {
    const filterValue = DispatchNewComponent._normalizeValue(value);
    return this.searchOptions.filter(searchValue => DispatchNewComponent._normalizeValue(searchValue).includes(filterValue));
  }

  private static _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  ngOnInit(): void {
    this.subject$ = new Subject<void>();
    this.timezone = this.dialogData.timezone;
    this.dispatch = this.dialogData.dispatch;
    this.date = moment().format();
    this.startTime = {hour: 0, minute: 0};
    this.durationTypes = ['H', 'M'];
    this.minDate = {
      year: moment().year(),
      month: moment().month() + 1 ,
      day: moment().date()
    };


    this.filteredOptions = this.searchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value)),
    );

    if (!this.timezone) {
      this.timezone = moment.tz.guess();
    }

    this.dispatch = this.dialogData.dispatch;
    if (this.dialogData.program) {
      this.program = this.dialogData.program;
    }

    if (this.dialogData.programs) {
      this.programs = this.dialogData.programs;
      for (const program of this.programs) {
        this.searchOptions.push(program.name);
      }
    }

    if (this.dispatch.startTimeUtc) {
      const convertedDateTime = moment(this.dispatch.startTimeUtc).tz(this.timezone).format();
      const dateStamp = convertedDateTime.split('T');
      this.date = moment(dateStamp[0]).utc().format();
      const time = dateStamp[1].split(':');
      this.startTime.hour = +time[0];
      this.startTime.minute = +time[1];
      const duration = this.dispatch.duration.replace(/PT(\d+)H(\d+)M/, '$1:$2').split(':');
      if (duration.length === 2) {
        this.parsedDuration = ( +duration[0] * 60 ) + (+duration[1]) + '';
      } else {
        this.parsedDuration = this.dispatch.duration.replace(/\D/g, '');
      }
      this.durationType = this.dispatch.duration.charAt(this.dispatch.duration.length - 1);
    } else {
      this.date = moment().utc().format();
      this.startTime.hour = moment().tz(this.timezone).hour();
      this.startTime.minute = moment().minute();
      this.durationType = 'M';
      this.pm = false;
    }

    const initialValue = new Date();
    initialValue.setHours(this.startTime.hour, this.startTime.minute, 0);
    this.dispatchForm = new FormGroup({
      dateTimeForm: new FormGroup({
        date: new FormControl(this.date, Validators.required),
        startTime: new FormControl(initialValue, Validators.required),
        timezone: new FormControl(this.timezone),
      }, this.validateEqual.bind(this)),
      timezone: new FormControl(this.timezone),
      duration: new FormControl(this.parsedDuration, Validators.required),
      durationType: new FormControl(this.durationType, Validators.required),
    });

   if (this.program) {
       this.dispatchForm.addControl('setpoint', new FormControl((this.dispatch.setpoint) ? this.dispatch.setpoint : this.program.properties.nominatedKw , Validators.required))
       this.dispatchForm.addControl('incentive',new FormControl((this.dispatch.incentive) ? this.dispatch.incentive : this.program.properties.programIncentive, [Validators.required, Validators.min(0)]))
       this.dispatchForm.addControl('penalty', new FormControl((this.dispatch.penalty) ? this.dispatch.penalty : this.program.properties.programPenalty, [Validators.required, Validators.min(0)]))
    } else {
      this.dispatchForm.addControl('targetId', new FormControl('', Validators.required));
      this.dispatchForm.addControl('setpoint' , new FormControl(0, Validators.required));
      this.dispatchForm.addControl('incentive', new FormControl(0, [Validators.required, Validators.min(0)]));
      this.dispatchForm.addControl('penalty', new FormControl(0, [Validators.required, Validators.min(0)]));
   }
  }

  filterProgram($event) {
    this.program = this.programs.find((program)=> {
      if (program.name === $event.option.value) {
        if (!ResourceService.canCreateDispatch(program)) {
          this.dialogRef.close({
            program: this.program,
            dispatch: null,
            status: errorStatus
          });
          return;
        }
        this.dispatchForm.get('targetId').setValue(program.id);
        this.programService.getProgramSites(program).subscribe(data => {
          this.dispatchForm.get('setpoint').setValue(this.program.properties.nominatedKw);
          this.dispatchForm.get('incentive').setValue(this.program.properties.programIncentive);
          this.dispatchForm.get('penalty').setValue(this.program.properties.programPenalty);
          if (data[0]) {
            this.timezone = data[0].timezone;
          }
        });
        return program;
      }
    });
  }

  validateEqual(formGroup: FormGroup): { [key: string]: boolean } {
      const value = formGroup.controls['startTime'].value;
      const date = formGroup.controls['date'].value;
      const timezone = formGroup.controls['timezone'].value;

      const momentFormDate = moment(date.year + '-' + date.month + '-' + date.day + ' ' + value.hour + ':' + value.minute).tz(timezone, true);

      if (!value) {
        return null;
      }

      if (momentFormDate.isBefore(moment().tz(timezone), 'minute') && this.dispatch.status != "ACTIVE") {
        return {tooEarly: true};
      }


      return null;
  }

  getDate() {
    return this.dispatchForm.get('date');
  }

  get penalty() {
    return this.dispatchForm.get('penalty');
  }

  get incentive() {
    return this.dispatchForm.get('incentive');
  }

  get setpoint() {
    return this.dispatchForm.get('setpoint');
  }

  ngOnDestroy(): void {
    this.subject$.next();
    this.subject$.complete();
  }

  close(): void {
    this.dialogRef.close({
      dispatch: this.dispatchForm.value,
      status: cancelStatus
    })
  }

  save(): void {
    if (!this.program) {
      for (const program of this.programs) {
        if (program.name == this.dispatchForm.get('targetId').value){
          this.dispatchForm.get('targetId').setValue(program.id);
        }
      }
    }
    this.dialogRef.close({
      dispatch: this.dispatchForm.value,
      program: this.program,
      timezone: this.timezone,
      status: saveStatus
    })
  }
}
